import React, { useEffect, useReducer, useRef } from 'react';
import { useDocumentViewerFiles } from './useDocumentViewerFiles';
import DocumentsViewerButton from './DocumentsViewerButton';
import { ClaimContext } from '../types';
import { useFlag } from '@unleash/proxy-client-react';

interface UseDocumentsViewerProps {
  claim: ClaimContext;
  workflowStep: string;
}

export function useDocumentsViewer(props: UseDocumentsViewerProps) {
  const { claim, workflowStep } = props;

  const [isDocumentsViewerOpen, toggleDocumentsViewer] = useReducer((state: boolean) => !state, false);

  const documentViewerFiles = useDocumentViewerFiles(claim);

  const isClaimsCenterNewSubmissionFlowEnabled = useFlag('claims-center-new-submission-flow');
  const isParcelFinderNewSubmissionFlowEnabled = useFlag('parcel-finder-new-submission-flow');

  const isNewFlow =
    (claim.entryPoint === 'parcel-finder' && isParcelFinderNewSubmissionFlowEnabled) ||
    (claim.entryPoint === 'claims-center' && isClaimsCenterNewSubmissionFlowEnabled);

  const isDocumentsViewerEnabled = isNewFlow;

  const documentsViewerButtonElement = isDocumentsViewerEnabled ? (
    <DocumentsViewerButton isViewerOpen={isDocumentsViewerOpen} onClick={toggleDocumentsViewer} />
  ) : null;

  const previousDocumentCount = useRef(documentViewerFiles.length);

  useEffect(() => {
    const isNotDelete = documentViewerFiles.length > previousDocumentCount.current;

    const shouldOpenDocumentsViewer =
      isDocumentsViewerEnabled && !isDocumentsViewerOpen && documentViewerFiles.length > 0 && isNotDelete;

    if (shouldOpenDocumentsViewer) {
      toggleDocumentsViewer();
    }

    previousDocumentCount.current = documentViewerFiles.length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowStep, isDocumentsViewerEnabled, documentViewerFiles]);

  return {
    documentsViewerButtonElement,
    documentViewerFiles,
    isDocumentsViewerEnabled,
    isDocumentsViewerOpen,
    toggleDocumentsViewer,
  };
}
